/**
 * Payment Success Component
 * Displays confirmation after successful payment
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { UserSubscription } from '@/types/subscription';

interface PaymentSuccessProps {
  subscription?: UserSubscription;
  planName?: string;
  amount?: number;
}

export function PaymentSuccess({
  subscription,
  planName = 'Seu plano',
  amount,
}: PaymentSuccessProps) {
  const [showConfetti, setShowConfetti] = useState(true);

  useEffect(() => {
    // Hide confetti after animation
    const timer = setTimeout(() => setShowConfetti(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="space-y-8">
      {/* Success Checkmark Animation */}
      {showConfetti && (
        <div className="flex justify-center mb-8">
          <div className="relative w-24 h-24 flex items-center justify-center">
            <div className="absolute w-24 h-24 bg-green-500/20 rounded-full animate-ping" />
            <div className="relative bg-green-500/10 border-2 border-green-500 rounded-full w-20 h-20 flex items-center justify-center">
              <svg
                className="w-10 h-10 text-green-500 animate-bounce"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      <div className="text-center space-y-4">
        <h2 className="text-3xl font-bold text-white">Pagamento Confirmado! 🎉</h2>
        <p className="text-white/60 text-lg">
          Sua assinatura foi ativada com sucesso.
        </p>
      </div>

      {/* Subscription Details */}
      <div className="bg-[#111111] border border-white/5 rounded-2xl p-8 space-y-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between pb-4 border-b border-white/5">
            <span className="text-white/70">Plano:</span>
            <span className="text-white font-semibold">{planName}</span>
          </div>
          
          {amount && (
            <div className="flex items-center justify-between pb-4 border-b border-white/5">
              <span className="text-white/70">Valor:</span>
              <span className="text-white font-semibold">
                R$ {(amount / 100).toFixed(2)}/mês
              </span>
            </div>
          )}

          {subscription && (
            <>
              <div className="flex items-center justify-between pb-4 border-b border-white/5">
                <span className="text-white/70">Status:</span>
                <span className="inline-block px-3 py-1 bg-green-500/20 border border-green-500/50 text-green-300 text-sm rounded-full">
                  ✓ Ativo
                </span>
              </div>

              {subscription.renews_at && (
                <div className="flex items-center justify-between">
                  <span className="text-white/70">Próxima Renovação:</span>
                  <span className="text-white font-semibold">
                    {new Date(subscription.renews_at).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Info Message */}
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
          <p className="text-sm text-blue-300/80">
            ℹ️ Um email de confirmação foi enviado para sua caixa de entrada. Guarde o recibo para seus registros.
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 pt-4">
        <Link
          to="/subscription-dashboard"
          className="flex-1 py-3 px-4 bg-[#C8A96E] text-[#0A0A0F] rounded-lg font-semibold hover:bg-[#D4B882] transition-colors text-center"
        >
          Ver Minha Assinatura
        </Link>
        <Link
          to="/"
          className="flex-1 py-3 px-4 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-lg font-semibold transition-colors text-center"
        >
          Voltar ao Início
        </Link>
      </div>

      {/* Additional Info */}
      <div className="text-center pt-4">
        <p className="text-sm text-white/40">
          Dúvidas? Entre em contato com nosso{' '}
          <Link to="/support" className="text-[#C8A96E] hover:underline">
            suporte
          </Link>
        </p>
      </div>
    </div>
  );
}
